import React, { useCallback, useEffect, useState } from "react";
import { Button, Space, Table, Tag, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CampusSummary } from "../../../../services/schools/campus.types";
import { getCampuses } from "../../../../services/schools/school.service";
import { getApiErrorMessage } from "../../../../utils";
import { useNotification } from "../../../../hooks/useNotification";
import CampusFormModal from "./CampusFormModal";
import { sortCampuses } from "./CampusesTab.utils";

const { Text } = Typography;

interface CampusesTabProps {
  /** Siempre la raíz: la tab sólo se renderiza cuando parent_id es null. */
  schoolId: number;
}

/**
 * Tab Planteles de la escuela raíz. La primera fila es la escuela principal
 * (tag "Principal") y no se edita desde acá: su identidad se administra en la
 * tab Escuela.
 */
const CampusesTab: React.FC<CampusesTabProps> = ({ schoolId }) => {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [campuses, setCampuses] = useState<CampusSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCampus, setEditingCampus] = useState<CampusSummary | null>(
    null
  );

  const loadCampuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCampuses(schoolId);
      setCampuses(sortCampuses(response));
    } catch (error) {
      notify(
        "error",
        "Error",
        getApiErrorMessage(error, "No se pudieron cargar los planteles.")
      );
    } finally {
      setLoading(false);
    }
    // notify viene de un hook sin identidad estable; incluirlo re-dispara la
    // carga en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  useEffect(() => {
    loadCampuses();
  }, [loadCampuses]);

  const handleAdd = () => {
    setEditingCampus(null);
    setModalOpen(true);
  };

  const handleEdit = (campus: CampusSummary) => {
    setEditingCampus(campus);
    setModalOpen(true);
  };

  const columns = [
    {
      title: "Plantel",
      dataIndex: "name",
      key: "name",
      render: (name: string, campus: CampusSummary) => (
        <Space>
          <span>{name}</span>
          {campus.is_main && <Tag color="blue">Principal</Tag>}
        </Space>
      ),
    },
    {
      title: "Abreviatura",
      dataIndex: "abbreviation",
      key: "abbreviation",
      render: (abbreviation: string | null) => abbreviation || "—",
    },
    {
      title: "CCT",
      dataIndex: "cct",
      key: "cct",
      render: (cct: string | null) => cct || "—",
    },
    { title: "Clases", dataIndex: "classroom_count", key: "classroom_count" },
    { title: "Alumnos", dataIndex: "student_count", key: "student_count" },
    { title: "Profes", dataIndex: "teacher_count", key: "teacher_count" },
    {
      title: "Acciones",
      key: "actions",
      render: (_: unknown, campus: CampusSummary) =>
        campus.is_main ? (
          <Text type="secondary">Se edita en la tab Escuela</Text>
        ) : (
          <Space>
            <Button type="link" onClick={() => handleEdit(campus)}>
              Editar
            </Button>
            <Button
              type="link"
              onClick={() =>
                navigate(`/schools/${campus.id}`, { state: campus.id })
              }
            >
              Abrir
            </Button>
          </Space>
        ),
    },
  ];

  return (
    <div className="campuses-container">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 16,
        }}
      >
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Agregar plantel
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={campuses}
        rowKey="id"
        loading={loading}
        pagination={false}
        locale={{ emptyText: "Esta escuela todavía no tiene planteles." }}
      />

      <CampusFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        rootSchoolId={schoolId}
        campuses={campuses}
        campus={editingCampus}
        onSaved={loadCampuses}
      />
    </div>
  );
};

export default CampusesTab;
